import {
  addTabInWorkAreaApi,
  addWorkAreaApi,
  deleteTabFromProjectApi,
  deleteWorkAreaFromProjectApi,
  getTabElementsByTabIdApi,
  getTabsByWorkAreaIdApi,
  getWorkAreasByProjectIdApi,
  setStateWindowElementApi,
  setStateWindowElementByEntityIdApi,
} from '@core/core/api'
import type {
  ContextObject,
  NeedIncludeHidden,
  TabInfo,
  TabObject,
  WorkAreaId,
} from '@core/core/types'
import type { TabType } from '@library/model'
import type {
  BaseObject,
  DomainType,
  ProjectId,
  PropertyType,
  TabElementInfo,
  TabElementStateType,
  TabId,
} from 'common/types'

import type { AppThunk } from '../store'
import {
  selectActiveTabIdInWorkArea,
  selectActiveWorkAreaId,
  selectAllWorkAreas,
  selectWorkAreaById,
} from './workAreaSelectors'
import { workAreaSlice } from './workAreaSlice'

export const { setActiveWorkArea, setActiveTabIdInWorkArea, clearAll, updateTreeElementStates } =
  workAreaSlice.actions
const { addWorkAreas, setTabDomain, addTabsToWorkArea, deleteWorkArea } = workAreaSlice.actions

const getTabsByWorkAreaId =
  (workAreaId: WorkAreaId, needIncludeHidden?: NeedIncludeHidden): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const tabs = await getTabsByWorkAreaIdApi(workAreaId, needIncludeHidden)
    const newTabs: TabObject[] = []
    if (tabs.length) {
      for (const tab of tabs) {
        const tabElementInfo = await getTabElementsByTabIdApi(tab.id)
        newTabs.push(convertToTabObject(tab, tabElementInfo))
      }

      const state = getState()
      const activeTabIdInWorkArea = selectActiveTabIdInWorkArea(state, workAreaId)

      if (!activeTabIdInWorkArea) {
        const tabId = newTabs[0].id
        dispatch(setActiveTabIdInWorkArea({ workAreaId, tabId }))
      }

      dispatch(addTabsToWorkArea({ workAreaId, tabs: newTabs }))
    }
  }

export const addTabToWorkArea =
  (
    workAreaId: WorkAreaId,
    tabType: TabType,
    name: string,
    vspId?: string,
    projectId?: ProjectId,
  ): AppThunk =>
  async dispatch => {
    const tabId = await addTabInWorkAreaApi(workAreaId, name, tabType, 0)
    if (tabId) {
      await dispatch(getTabsByWorkAreaId(workAreaId, false))
      dispatch(setActiveTabIdInWorkArea({ workAreaId, tabId }))
    }
    if (vspId && projectId) {
      dispatch(setTabElementState(projectId, vspId, tabId, 'Checked'))
    }
  }

export const deleteTabFromProject =
  (workAreaId: WorkAreaId, tabId: TabId): AppThunk =>
  async (dispatch, getState) => {
    const isDeleteTab = await deleteTabFromProjectApi(tabId)
    const tabs = selectWorkAreaById(getState(), workAreaId)?.tabs

    if (isDeleteTab && Array.isArray(tabs)) {
      const newTabs = tabs.filter(({ id }) => id !== tabId)
      const newActiveTabId = newTabs[0]?.id ?? null
      dispatch(setActiveTabIdInWorkArea({ workAreaId, tabId: newActiveTabId }))
      dispatch(addTabsToWorkArea({ workAreaId, tabs: newTabs }))
    }
  }

export const deleteOtherTabsFromProject =
  (workAreaId: WorkAreaId, tabId: TabId): AppThunk =>
  async (dispatch, getState) => {
    const tabs = selectWorkAreaById(getState(), workAreaId)?.tabs
    if (!tabs) return

    const tabsToDelete = tabs
      .filter(({ id }) => id !== tabId)
      .map(({ id }) => deleteTabFromProjectApi(id))

    await Promise.all(tabsToDelete)
    const newTabs = tabs.filter(({ id }) => id === tabId)
    dispatch(setActiveTabIdInWorkArea({ workAreaId, tabId }))
    dispatch(addTabsToWorkArea({ workAreaId, tabs: newTabs }))
  }

export const deleteTabsFromProject =
  (workAreaId: WorkAreaId): AppThunk =>
  async (dispatch, getState) => {
    const tabs = selectWorkAreaById(getState(), workAreaId)?.tabs.map(({ id }) =>
      deleteTabFromProjectApi(id),
    )
    if (!tabs) return

    await Promise.all(tabs)

    dispatch(setActiveTabIdInWorkArea({ workAreaId, tabId: null }))
    dispatch(addTabsToWorkArea({ workAreaId, tabs: [] }))
  }

export const getWorkAreasByProjectId =
  (projectId: ProjectId, needIncludeHidden?: NeedIncludeHidden): AppThunk =>
  async (dispatch, getState) => {
    const workAreas = await getWorkAreasByProjectIdApi(projectId, needIncludeHidden)
    const workAreaId = workAreas.at(0)?.id

    if (workAreaId) {
      dispatch(addWorkAreas(workAreas))
      const activeWorkAreaId = selectActiveWorkAreaId(getState())
      if (!activeWorkAreaId) {
        dispatch(setActiveWorkArea(workAreaId))
      }

      workAreas.forEach(({ id }) => {
        dispatch(getTabsByWorkAreaId(id, needIncludeHidden))
      })
    } else {
      dispatch(addWorkArea(projectId))
    }
  }

export const addWorkArea =
  (projectId: ProjectId): AppThunk =>
  async dispatch => {
    if (projectId !== '') {
      const workAreaId: WorkAreaId | undefined = await addWorkAreaApi(projectId)

      if (workAreaId) {
        dispatch(getWorkAreasByProjectId(projectId))
      }
    }
  }

export const deleteWorkAreaFromProject =
  (workAreaId: WorkAreaId): AppThunk =>
  async (dispatch, getState) => {
    const isDeleted = await deleteWorkAreaFromProjectApi(workAreaId)
    if (isDeleted) {
      dispatch(deleteWorkArea(workAreaId))

      const workAreas = selectAllWorkAreas(getState())
      const firstWorkArea = workAreas.at(0)
      if (firstWorkArea) {
        dispatch(setActiveWorkArea(firstWorkArea.id))
      }
    }
  }

export const setTabElementState =
  (
    projectId: ProjectId,
    projectElementId: string,
    tabId: TabId,
    state: TabElementStateType,
  ): AppThunk =>
  async dispatch => {
    const tabInfos = await setStateWindowElementApi(projectId, projectElementId, tabId, state)

    dispatch(updateTreeElementStates(tabInfos))
  }

export const setTabElementStateByEntityId =
  (
    projectId: ProjectId,
    entityId: string,
    entityType: BaseObject,
    tabId: TabId,
    state: TabElementStateType,
  ): AppThunk =>
  async dispatch => {
    const tabInfos = await setStateWindowElementByEntityIdApi(
      projectId,
      entityId,
      entityType,
      tabId,
      state,
    )

    dispatch(updateTreeElementStates(tabInfos))
  }

export const updateTabDomain =
  (workAreaId: WorkAreaId, tabId: TabId, domainType: DomainType): AppThunk =>
  dispatch => {
    dispatch(setTabDomain({ workAreaId, tabId, domainType }))
  }

function convertToTabObject(tab: TabInfo, elementInfo: TabElementInfo[]): TabObject {
  return {
    id: tab.id,
    displayName: tab.displayName,
    type: tab.type,
    halfContext: elementInfo
      .filter(elem => elem.state === 'Indeterminate')
      .map(convertToContextObject),
    context: elementInfo.filter(elem => elem.state === 'Checked').map(convertToContextObject),
    domainType: tab.domainType,
  }
}

function convertToContextObject(elementInfo: TabElementInfo): ContextObject {
  const getCustomProp = (propType: PropertyType) =>
    elementInfo.elementSettings.find(elem => elem.propertyType === propType)?.value

  return {
    entityId: elementInfo.projectEntityId,
    treeId: elementInfo.projectElementId,
    entityName: elementInfo.elementName,
    entityType: elementInfo.elementType,
    customProperties: {
      fillColor: getCustomProp('FillColor'),
      strokeColor: getCustomProp('StrokeColor'),
      fontSize: getCustomProp('FontSize'),
    },
  }
}
